<template>
    <div class="input-code">
        <div
            class="code"
            :class="{ disabled: disabled, error: isError }"
        >
            <input
                v-for="i in range(0, length)"
                :key="`input-digits-${i}`"
                ref="refs"
                :value="state[i]"
                maxlength="1"
                :data-testid="`input-digits-${i}`"
                :class="{ filled: !!state[i] }"
                :disabled="disabled"
                @keydown="(e) => handleKey(e, i)"
                @focus="() => handleFocus(i)"
                @input="(e) => handleInput(e, i)"
                @paste.stop="handlePaste"
            />
        </div>
        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import ErrorText from 'widget-components/components/ErrorText'
import { promiseTimeout } from '@vueuse/core'
import { range } from 'ramda'

const props = withDefaults(defineProps<{
    modelValue: string
    length?: number | string
    error?: string | boolean
    focus?: boolean
    disabled?: boolean
    fieldName?: string
    initialValue?: string
}>(), {
    length: 5,
})

const keys = {
    backspace: 8,
    tab: 9,
    left: 37,
    up: 38,
    right: 39,
    down: 40,
    enter: 13,
}

const emit = defineEmits(['update:modelValue', 'submit'])

const disabled = computed(() => props.disabled)
const length = computed(() => +props.length)

const model = computed(() => {
    const value = props.modelValue
    const out = []
    for (let i = 0; i < length.value; i += 1) {
        const letter = value && value[i] !== ' ' ? value[i] : ''
        out.push(letter)
    }
    return out
})

const state = ref(model.value)
const isPasting = ref(false)
const refs = ref<HTMLInputElement[]>([])

const handleFocus = (index: number) => {
    if (isPasting.value) return
    state.value[index] = ''
}

const handleKey = async (event: KeyboardEvent, index: number) => {
    if (isPasting.value) return

    switch (event.keyCode) {
        case keys.enter:
            emit('submit')
            break
        case keys.backspace:
        case keys.left:
        case keys.down:
            event.preventDefault()
            if (index !== 0) {
                refs.value[index - 1].focus()
            }
            break
        case keys.right:
        case keys.up:
            event.preventDefault()
            if (index !== length.value - 1) {
                refs.value[index + 1].focus()
            }
            break
        default:
    }
}

const handleInput = async (event: Event, index: number) => {
    const isLast = index === length.value - 1
    if (event.type === 'insertFromPaste' || isPasting.value) {
        return
    }

    state.value[index] = (event.target as HTMLInputElement | undefined)?.value[0] ?? ' '

    if (isLast) {
        await nextTick()
        refs.value[index].blur()
        await nextTick()
        emit('submit')
    } else {
        await nextTick()
        refs.value[index + 1].focus()
    }
}

const handlePaste = async (event: ClipboardEvent) => {
    const value = event.clipboardData?.getData('text/plain').trim().split('')
    isPasting.value = true
    if (value?.length) {
        for (let index = 0; index < refs.value.length; index++) {
            state.value[index] = value[index]
        }
    }
    await promiseTimeout(100)
    isPasting.value = false

    if (state.value.length === refs.value.length) {
        refs.value.forEach(r => r.blur())
        await nextTick()
        emit('submit')
    }
}

const isError = computed(() => !!props.error)

onMounted(async () => {
    if (props.focus) {
        await nextTick()
        for (const i in range(0, length.value)) {
            if (!state.value[i] && state.value[i] !== ' ' && refs.value[i]) {
                refs.value[i].focus()
                break
            }
        }
    }

    if (props.initialValue) {
        const values = props.initialValue.split('')

        state.value.forEach((_, i) => {
            const value = values[i] || ''
            state.value[i] = value
            refs.value[i].value = value
        })
    }
})

watch(disabled, (val) => {
    if (!val) {
        refs.value.forEach((i) => {
            i.blur()
        })
    }
})

watch(
    () => [...model.value],
    (val) => {
        val.forEach((_, i) => {
            const l = val[i] === ' ' ? '' : val[i]
            if (l !== state.value[i]) state.value[i] = l
        })
    },
)

watch(
    () => [...state.value],
    (val) => {
        const newVal = val.map((i) => i || ' ').join('')
        emit('update:modelValue', newVal)
    },
)
</script>

<style lang="sass" scoped>
@import "./InputDigitsRaw.sass"
</style>
